<template>
    <v-dialog v-model="show" scrollable max-width="50%">
        <v-form>
            <v-card class="pa-8" v-if="!isLoading">
                <v-card-title class="mb-8 d-flex align-end">
                    <span class="text-h4 font-weight-medium mr-4">
                        Brokerage account
                    </span>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col>
                            <individuals-dropdown :individualsDropdown="individualsDropdown" v-model="form"></individuals-dropdown>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-text-field
                                v-model="form.company_name"
                                label="Held at"
                                required
                                :error-messages="form.errors.get('company_name')"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-select
                                :items="typeLabels"
                                label="Type of account"
                                v-model="form.type_of_account_cd"
                                :error-messages="form.errors.get('type_of_account_cd')"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-text-field
                                v-model="form.account_number"
                                label="Account number"
                                :readonly="isLinked"
                                :error-messages="form.errors.get('account_number')"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-currency-field
                                label="Account balance"
                                :readonly="isLinked"
                                v-model="form.value"
                                :error-messages="form.errors.get('value')"
                            ></v-currency-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-menu
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="openDate"
                                        label="Open Date"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        @blur="form.open_date = parseDate(openDate)"
                                        :error-messages="form.errors.get('open_date')"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="openDate"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-menu
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="startDate"
                                        label="Start Date"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        @blur="form.start_date = parseDate(startDate)"
                                        :error-messages="form.errors.get('start_date')"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="startDate"
                                ></v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-currency-field
                                v-model="form.deposits"
                                label="Deposits"
                                :readonly="isLinked"
                                :error-messages="form.errors.get('deposits')"
                            ></v-currency-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-currency-field
                                v-model="form.withdrawals"
                                label="Withdrawals"
                                :readonly="isLinked"
                                :error-messages="form.errors.get('withdrawals')"
                            ></v-currency-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-currency-field
                                v-model="form.account_start_value"
                                label="Account start value"
                                :error-messages="form.errors.get('account_start_value')"
                            ></v-currency-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-text-field
                                v-model="form.source_of_funds"
                                label="Source of funds"
                                :error-messages="form.errors.get('source_of_funds')"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-text-field
                                v-model="form.inv_strategy"
                                label="Inv strategy"
                                :error-messages="form.errors.get('inv_strategy')"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="text-center mt-12">
                            <v-btn color="primary" depressed rounded height="48" width="300" @click="save">
                                Save
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card class="pa-8" v-else>
                <v-progress-circular
                    class="d-flex mx-auto"
                    indeterminate
                    size="50"
                    color="primary"
                >
                </v-progress-circular>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script>
import { Form } from 'vform'
import {mapState} from "vuex";
import IndividualsDropdown from "@/components/IndividualsDropdown";
import dayjs from 'dayjs';
import VCurrencyField from "@/components/VCurrencyField";

export default {
    components: {
        IndividualsDropdown,
        VCurrencyField,
    },
    props: {
        value: {
            required: true,
            type: Boolean
        },
        householdId: {
            required: true,
            type: Number
        },
        brokerageAccount: {
            type: [Object, null]
        },
    },
    data() {
        return {
            form: new Form({
                id: null,
                owner: null,
                individual_id: null,
                beneficiary_id: null,
                company_name: null,
                type_of_account_cd: null,
                account_number: null,
                value: 0,
                open_date: null,
                start_date: null,
                deposits: 0,
                withdrawals: 0,
                account_start_value: 0,
                source_of_funds: null,
                inv_strategy: null,
                return_percentage: 0,
            }),
            openDate: null,
            startDate: null,
            isLoading: false,
        }
    },
    computed: {
        ...mapState('individuals', ['individualsDropdown']),
        ...mapState('constants', ['typeLabels']),
        ...mapState('auth', ['user']),
        show: {
            get () {
                return this.value
            },
            set (value) {
                this.$emit('input', value)
            }
        },
        isLinked() {
            if (this.brokerageAccount === null) {
                return false;
            }

            return this.brokerageAccount.external_account_id !== undefined && this.brokerageAccount.external_account_id !== null;
        }
    },
    mounted() {
        if (this.brokerageAccount !== null) {
            this.form.fill(this.brokerageAccount);
            this.openDate = this.form.open_date;
            this.startDate = this.form.start_date;
        }
    },
    methods: {
        save() {
            this.form.start_date = this.parseDate(this.startDate);
            this.form.open_date = this.parseDate(this.openDate);

            this.isLoading = true;

            if (this.form.id === null) {
                this.form.post('/api/households/' + this.user.household_id + '/account-manager-brokerage-accounts', { showLoader: false })
                    .then(() => this.postSave())
                    .catch(() => this.isLoading = false);
            } else {
                this.form.put('/api/households/' + this.user.household_id + '/account-manager-brokerage-accounts/' + this.form.id, { showLoader: false })
                    .then(() => this.postSave())
                    .catch(() => this.isLoading = false);
            }
        },
        postSave() {
            this.$emit('reload');
            this.show = this.isLoading = false;
            this.form.reset();
        },
        parseDate(date) {
            if (!date) return null;

            return dayjs(date).format('MM/DD/YYYY');
        }
    },
}
</script>
